import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { GFFrame } from "@/components/GFFrame";
import { getQuestions, submitAssessment } from "@/lib/gfApi";

const FACTOR_ACCENT = {
  hala_tuju: "Hala Tuju",
  jenama: "Jenama",
  pasaran: "Pasaran",
  hasil: "Hasil",
  pelaksanaan: "Pelaksanaan",
};

export default function Assessment() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const user = sessionStorage.getItem("gf_user");
    if (!user) {
      navigate("/mula");
      return;
    }
    getQuestions()
      .then(setData)
      .catch(() => toast.error("Gagal memuatkan soalan. Sila cuba lagi."));
  }, [navigate]);

  if (!data) {
    return (
      <GFFrame testId="assessment-loading">
        <div className="flex min-h-screen items-center justify-center text-slate-400">
          Memuatkan soalan…
        </div>
      </GFFrame>
    );
  }

  const total = data.total;
  const q = data.questions[idx];
  const current = answers[q.id];
  const progress = ((idx + (current ? 1 : 0)) / total) * 100;

  const choose = (value) => {
    const next = { ...answers, [q.id]: value };
    setAnswers(next);
    setTimeout(() => {
      if (idx < total - 1) {
        setIdx(idx + 1);
      } else {
        finish(next);
      }
    }, 220);
  };

  const finish = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const orderedAnswers = data.questions.map((qq) => finalAnswers[qq.id]);
      const user = JSON.parse(sessionStorage.getItem("gf_user"));
      const result = await submitAssessment({ user, answers: orderedAnswers });
      navigate(`/result/${result.id}`);
    } catch (e) {
      setSubmitting(false);
      toast.error("Gagal menghantar jawapan. Sila cuba lagi.");
    }
  };

  const goBack = () => {
    if (idx > 0) setIdx(idx - 1);
    else navigate("/mula");
  };

  return (
    <GFFrame testId="assessment-page">
      <div className="flex min-h-screen flex-col">
        {/* Progress */}
        <div className="px-6 pt-8">
          <div className="mb-3 flex items-center justify-between">
            <button
              onClick={goBack}
              data-testid="assessment-back-btn"
              className="flex items-center gap-1 text-sm text-slate-400"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <span
              data-testid="assessment-progress-label"
              className="text-xs font-semibold uppercase tracking-widest text-[#D4AF37]"
            >
              {FACTOR_ACCENT[q.factor]}
            </span>
            <span className="text-sm font-bold text-slate-500">
              {idx + 1}/{total}
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <motion.div
              className="h-full rounded-full bg-[#D4AF37]"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
          <p className="mt-3 text-xs text-slate-400">
            Soalan {idx + 1} daripada {total}
          </p>
        </div>

        {/* Question */}
        <div className="flex flex-1 flex-col px-6 py-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="flex flex-1 flex-col"
            >
              <div className="flex flex-1 items-center">
                <h2 className="font-heading text-2xl font-semibold leading-snug text-[#0F172A]">
                  {q.text}
                </h2>
              </div>

              <div className="space-y-2.5 pb-6">
                {data.scale.map((s) => {
                  const active = current === s.value;
                  return (
                    <button
                      key={s.value}
                      data-testid={`scale-option-${s.value}`}
                      onClick={() => choose(s.value)}
                      className={`flex w-full items-center gap-4 rounded-2xl border px-4 py-3.5 text-left transition-all duration-150 active:scale-[0.99] ${
                        active
                          ? "border-[#0F172A] bg-[#0F172A] text-white"
                          : "border-[#E7E2D6] bg-white text-[#0F172A] hover:border-[#D4AF37]"
                      }`}
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                          active ? "bg-[#D4AF37] text-[#0F172A]" : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {s.value}
                      </span>
                      <span className="text-[15px] font-medium">{s.label}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {submitting && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/85 backdrop-blur-sm">
            <div className="text-center">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-[#D4AF37]" />
              <p className="mt-4 text-sm font-semibold text-slate-500">
                Mengira Growth Factor anda…
              </p>
            </div>
          </div>
        )}
      </div>
    </GFFrame>
  );
}
